import Vue from 'vue';
import VueRouter from 'vue-router';
import { isDesktop } from '@mgo/core/src/utils/deviceType';
import {
  isCoacJourneyActive,
  isDisputeJourneyActive,
} from '@mgo/core/src/journeyState';
import { page } from '../analytics/events';

Vue.use(VueRouter);

/**
 * COAC journey route guard.
 *
 * This is navigation state only. It does not authorize the route or API call;
 * the BFF session and ICOA grant are authoritative for every request.
 * @param {Object} to - the target Route Object being navigated to
 * @param {Object} from - the current route being navigated away from
 * @param {Function} next - move on to the next hook or confirm navigation if no more hooks
 */
export const coacJourneyOnly = async (to, from, next) => {
  if (!isCoacJourneyActive()) {
    next({ name: 'home' });
  } else {
    next();
  }
};

/**
 * Dispute navigation is separate from a customer COAC grant. This is also a
 * UX-only guard; ICOA remains responsible for authorizing every request.
 */
export const disputeJourneyOnly = async (to, from, next) => {
  if (!isDisputeJourneyActive()) {
    next({ name: 'disputes' });
  } else {
    next();
  }
};

const baseRoute = 'mgoc';

const routes = [
  {
    path: '/',
    redirect: { name: 'home' },
  },
  {
    path: `/${baseRoute}/home`,
    name: 'home',
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'bootstrap' */ '../views/Home/desktop.vue')
      : import(/* webpackChunkName: 'bootstrap' */ '../views/Home/mobile.vue')),
  },
  {
    path: `/${baseRoute}/review`,
    name: 'review',
    beforeEnter: coacJourneyOnly,
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'review' */ '../views/Review/desktop.vue')
      : import(/* webpackChunkName: 'review' */ '../views/Review/mobile.vue')),
  },
  {
    path: `/${baseRoute}/edit`,
    name: 'edit',
    beforeEnter: coacJourneyOnly,
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'review' */ '../views/Edit/desktop.vue')
      : import(/* webpackChunkName: 'review' */ '../views/Edit/mobile.vue')),
  },
  {
    path: `/${baseRoute}/fatal-error`,
    name: 'fatal-error',
    component: () => import(/* webpackChunkName: 'error' */ '../views/FatalError.vue'),
  },
  {
    path: `/${baseRoute}/processing-error`,
    name: 'processing-error',
    component: () => import(/* webpackChunkName: 'error' */ '../views/ProcessingError.vue'),
  },
  {
    path: `/${baseRoute}/disputes`,
    name: 'disputes',
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'disputes' */ '../views/Disputes/desktop.vue')
      : import(/* webpackChunkName: 'disputes' */ '../views/Disputes/mobile.vue')),
  },
  {
    path: `/${baseRoute}/disputes/questionnaire`,
    name: 'disputes-questionnaire',
    beforeEnter: disputeJourneyOnly,
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'disputes' */ '../views/Questionnaire/desktop.vue')
      : import(/* webpackChunkName: 'disputes' */ '../views/Questionnaire/mobile.vue')),
  },
  {
    path: `/${baseRoute}/cancel`,
    name: 'cancel',
    beforeEnter: coacJourneyOnly,
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'bootstrap' */ '../views/Cancel/desktop.vue')
      : import(/* webpackChunkName: 'bootstrap' */ '../views/Cancel/mobile.vue')),
  },
  {
    path: `/${baseRoute}/confirm-cancel`,
    // will need a query param with the reason code passed in
    // ex: https://coactst02.imagitas.com/mgoc/confirm-cancel?reason=CAN
    name: 'confirm-cancel',
    beforeEnter: coacJourneyOnly,
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'bootstrap' */ '../views/ConfirmCancel/desktop.vue')
      : import(/* webpackChunkName: 'bootstrap' */ '../views/ConfirmCancel/mobile.vue')),
  },
  {
    path: `/${baseRoute}/extended-mail-forwarding`,
    name: 'extended-mail-forwarding',
    beforeEnter: coacJourneyOnly,
    component: () => (isDesktop()
      ? import(/* webpackChunkName: 'emf' */ '../views/ExtendedMailForwarding/desktop.vue')
      : import(/* webpackChunkName: 'emf' */ '../views/ExtendedMailForwarding/mobile.vue')),
  },
  {
    path: `/${baseRoute}/action`,
    name: 'action',
    beforeEnter: coacJourneyOnly,
    component: () => import(/* webpackChunkName: 'payment' */ '../views/Action.vue'),
  },
  {
    path: `/${baseRoute}/session-unavailable`,
    name: 'session-unavailable',
    component: () => import(/* webpackChunkName: 'error' */ '../views/SessionUnavailable.vue'),
  },
  {
    path: '*',
    redirect: '/',
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.afterEach((to) => {
  const { path, query, meta: { pageName, isMobileDevice } } = to;
  // FE analytics pageView event
  page(
    pageName,
    {
      path,
      pageName,
      query,
      isMobileDevice,
      title: window.document.title.replace(/[^a-z0-9\- ]/gi, ''),
      url: window.location.href,
    },
  );
});

export default router;
